import { Box, Button, Typography } from '@material-ui/core';
import Accordion from '@material-ui/core/ExpansionPanel';
import AccordionDetails from '@material-ui/core/ExpansionPanelDetails';
import AccordionSummary from '@material-ui/core/ExpansionPanelSummary';
import { isEqual } from 'lodash';
import { reaction } from 'mobx';
import { useObserver } from 'mobx-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  FB,
  FBInput,
  FBInputProps,
  FBMediaProps,
  FBMPIProcedureItem,
  FBMPIProcedureState,
  FBProcedureItemType,
  FBProvider,
  FBSchemaProps,
  FBSchemaState,
  FBStarter,
  FBTextEditor,
} from '..';
import { ReactComponent as CollapseMoreIcon } from '../../../assets/images/CollapseMoreIcon.svg';
import { ReactComponent as ExpandMoreIconCustom } from '../../../assets/images/ExpandMoreIcon.svg';
import { documentRevisionsSelectors } from '../../../state/ducks/documentRevisions';
import { ApplicationState } from '../../../state/reducers';
import FBDeviationProcedureState from './FBDeviationProcedure.state';
import useDeviationProcedureStyles from './FBDeviationProcedure.styles';
import { FBDeviationProcedureProps, FBDeviationStepValue, isDeviationProcedureStep } from './FBDeviationProcedure.types';
import { withFBDeviationProcedure } from './FBDeviationProcedure.wrap';

interface StepProps {
  mpiId: string
  stepId: string
  stepData: FBDeviationStepValue
  name: string
  disabled?: boolean
  isPreview?: boolean
  isOutput?: boolean
  mpiProcedureState: FBMPIProcedureState
}

const FBDeviationProcedureStep: React.FC<StepProps> = ({
  mpiId,
  stepId,
  stepData,
  name,
  disabled,
  isPreview,
  isOutput,
  mpiProcedureState,
}) => {
  const classes = useDeviationProcedureStyles();
  const { formState } = FB.useStores();
  const stepName = `${name}.${mpiId}.${stepId}`;

  // Seed form state synchronously on first render so FBTextEditor (withState)
  // picks up the existing description before it initializes its internal state.
  const seeded = React.useRef(false);
  if (!seeded.current) {
    seeded.current = true;
    if (stepData.description && !formState?.getFieldValue(`${stepName}.description`)) {
      formState?.setFieldValue(`${stepName}.description`, stepData.description);
    }
  }

  React.useEffect(() => reaction(
    () => formState?.inputState.get(`${stepName}.description`)?.value,
    (desc) => {
      mpiProcedureState.value = { ...mpiProcedureState.value, description: desc as string };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);

  React.useEffect(() => reaction(
    () => formState?.inputState.get(`${stepName}.attachments`)?.value,
    (attachments) => {
      const { attachments: stateAttachments } = mpiProcedureState.value ?? {};
      const incoming = (attachments as FBMediaProps[]) ?? [];
      // eslint-disable-next-line @typescript-eslint/require-array-sort-compare
      if (isEqual([...incoming].sort(), [...(stateAttachments ?? [])].sort())) { return; }
      mpiProcedureState.value = { ...mpiProcedureState.value, attachments: incoming };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);

  const itemTypes = [
    FBProcedureItemType.attachments,
    FBProcedureItemType.materials,
    FBProcedureItemType.equipment,
  ];

  return (
    <FBProvider {...{ mpiProcedureState }}>
      <Box mb={2}>
        <Typography variant="caption" className={classes.stepCaption}>
          {`Sequence #${stepData.stepIndex ?? ''}`}
        </Typography>
        <FBTextEditor
          name={`${stepName}.description`}
          gutter={false}
          autosave={false}
          shouldNotAutosaveOnBlur={true}
          withState
          disabled={false}
          removeBorder={false}
        />
        {itemTypes.map((type) => (
          <Box key={type} mt={2}>
            <FBMPIProcedureItem
              {...{ type, name: stepName, disabled, isPreview, isOutput }}
              isDeviationMode={true}
            />
          </Box>
        ))}
      </Box>
    </FBProvider>
  );
};

interface MpiComponentProps {
  mpiId: string
  stepId: string
  stepData: FBDeviationStepValue
  name: string
  disabled?: boolean
  mpiSchema: FBSchemaProps[]
  position: number
  canEdit?: boolean
  onRemove?: () => void
  onMove?: (direction: -1 | 1) => void
  canMoveUp?: boolean
  canMoveDown?: boolean
}

// Renders a cloned non-procedure MPI component with its real control. Controls look themselves up by index/name in
// workspaceState.schema, so give them a workspace whose schema is the MPI's.
const FBDeviationMpiComponent: React.FC<MpiComponentProps> = ({
  stepId, name, mpiSchema, position, mpiId, stepData, canEdit, onRemove, onMove, canMoveUp, canMoveDown,
}) => {
  const { workspaceState } = FB.useStores();
  // Keep the Proxy stable: a new workspace on each mpiSchema change makes the MobX Provider throw
  // "set of provided stores has changed". Read the live values through a ref instead.
  const latest = React.useRef({ mpiSchema, position });
  latest.current = { mpiSchema, position };
  const mpiWorkspace = React.useMemo(() => workspaceState && new Proxy(workspaceState, {
    get: (target, prop) => {
      const { mpiSchema, position } = latest.current;
      switch (prop) {
        case 'schema': return mpiSchema;
        case 'getSchemaItemAt': return (i?: number) => (i === undefined ? undefined : mpiSchema[i]);
        case 'getSchemaItem': return (n?: string) => mpiSchema.find((it) => it.name === n);
        case 'saveDocRev': return () => undefined; // edits are persisted via the deviation's formInput
        case 'setSchemaItem': return () => undefined; // never write cloned items into the deviation's own schema
        // blanktplform is only editable in 'design' mode; deviations author their own template files
        case 'mode': return mpiSchema[position]?.type === 'blanktplform' ? 'design' : Reflect.get(target, prop);
        default: {
          const value = Reflect.get(target, prop);
          return typeof value === 'function' ? value.bind(target) : value;
        }
      }
    },
  }), [workspaceState]);

  return (
    <FBProvider workspaceState={mpiWorkspace}>
      {FBSchemaState.component({
        ...mpiSchema[position],
        gutter: false,
        autosave: false,
        // same as the MPI: cloned components are locked except the blank template form; added ones are editable
        disabled: !canEdit || Boolean(
          (!stepData.added && mpiSchema[position]?.type !== 'blanktplform')
          || mpiSchema[position]?.disabled
          || mpiSchema[position]?.outputDisabled,
        ),
        key: `${mpiId}-${stepId}`,
      } as FBInputProps)}
      {stepData.added && canEdit && (
        <Box display="flex" alignItems="center">
          <Button size="small" disabled={!canMoveUp} onClick={() => onMove?.(-1)}>Move up</Button>
          <Button size="small" disabled={!canMoveDown} onClick={() => onMove?.(1)}>Move down</Button>
          <Button size="small" onClick={onRemove}>Remove component</Button>
        </Box>
      )}
    </FBProvider>
  );
};

interface MpiSectionProps {
  mpiId: string
  mpiIndex: number
  steps: Record<string, FBDeviationStepValue>
  name: string
  disabled?: boolean
  isPreview?: boolean
  isOutput?: boolean
  canEdit?: boolean
  deviationProcedureState?: FBDeviationProcedureState
}

const FBDeviationMpiSection: React.FC<MpiSectionProps> = ({
  mpiId, mpiIndex, steps, name, disabled, isPreview, isOutput, canEdit, deviationProcedureState,
}) => {
  const classes = useDeviationProcedureStyles();
  const [expanded, setExpanded] = useState(false);
  // jsonb does not keep key order: restore the MPI's component order
  const stepEntries = Object.entries(steps).sort(
    ([, a], [, b]) => ((a.schemaItem?.index as number) ?? 1e6) - ((b.schemaItem?.index as number) ?? 1e6),
  );
  const shownCount = stepEntries.filter(([, it]) => isDeviationProcedureStep(it) || it.added).length;
  // The MPI's own schema (nested names, no tabs) so index-based controls resolve against it, not the deviation's schema
  const mpiSchema = React.useMemo(() => stepEntries.map(([stepId, step], index) => ({
    ...step.schemaItem,
    type: step.type,
    label: step.label,
    name: `${name}.${mpiId}.${stepId}`,
    tabId: undefined,
    index,
  })) as FBSchemaProps[],
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [mpiId, name, stepEntries.map(([id]) => id).join()]); // sorted order: it changes when a component is moved

  const docRevision = useSelector((state: ApplicationState) =>
    documentRevisionsSelectors.getDocumentRevision(state, mpiId),
  );

  const docId = docRevision?.document?.docId;
  const docName = docRevision?.name;
  const mpiLabel = docId && docName ? `${docId} - ${docName}` : (docId ?? docName);

  // Use mpiIndex from step data (all steps in same MPI share the same mpiIndex)
  const lhrStepIndex = stepEntries[0]?.[1]?.mpiIndex ?? mpiIndex;
  const stepLabel = `LHR Step ${lhrStepIndex}`;

  const isDeviated = useObserver(() => deviationProcedureState?.deviatedMpiIds.has(mpiId) ?? false);

  return (
    <Accordion
      className={classes.panelBorder}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary
        className={classes.panelSummary}
        expandIcon={expanded ? <ExpandMoreIconCustom /> : <CollapseMoreIcon />}
      >
        <Box className={classes.labelContainer}>
          <Box display="flex" alignItems="center" flexGrow={1} overflow="hidden">
            <Typography variant="subtitle2" className={classes.stepLabelText}>
              {stepLabel}
            </Typography>
            {mpiLabel && (
              <Typography
                variant="subtitle2"
                className={classes.mpiLabelText}
              >
                {`(${mpiLabel})`}
              </Typography>
            )}
            {isDeviated && (
              <Typography className={classes.deviatedBadge}>
                DEVIATED
              </Typography>
            )}
          </Box>
          <Typography className={classes.stepCountLabel}>
            {`${shownCount} Item${shownCount !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {stepEntries.map(([stepId, stepData], position) => {
          if (!isDeviationProcedureStep(stepData)) {
            // Cloned MPI components stay in the schema (the LHR step needs them) but are not shown here
            if (!stepData.added) { return null; }
            // Neighbours as the user sees them (only added components are rendered)
            const visible = stepEntries.filter(([, it]) => it.added && !it.schemaItem?.hidden);
            const at = visible.findIndex(([id]) => id === stepId);
            const idx = (i: number) => Number(visible[i]?.[1].schemaItem?.index) || 0;
            const onMove = (dir: -1 | 1) => {
              const target = at + dir;
              const beyond = target + dir;
              // land between the neighbour and the one after it (or just past the neighbour at either end)
              const next = visible[beyond] ? (idx(target) + idx(beyond)) / 2 : idx(target) + dir;
              deviationProcedureState?.setComponentIndex(mpiId, stepId, next);
            };
            return (
              <Box key={stepId} mb={2}>
                <FBDeviationMpiComponent
                  {...{ mpiId, stepId, stepData, name, mpiSchema, position, canEdit }}
                  onRemove={() => deviationProcedureState?.removeComponent(mpiId, stepId)}
                  {...{ onMove }}
                  canMoveUp={at > 0}
                  canMoveDown={at >= 0 && at < visible.length - 1}
                />
              </Box>
            );
          }
          const stepState = deviationProcedureState?.getStepState(mpiId, stepId);
          if (!stepState) { return null; }

          return (
            <FBDeviationProcedureStep
              key={stepId}
              {...{ mpiId, stepId, stepData, name, disabled, isPreview, isOutput }}
              mpiProcedureState={stepState}
            />
          );
        })}
        {canEdit && (
          <Box
            mt={1}
            onClickCapture={() => {
              FBDeviationProcedureState.onAdd = (item) => deviationProcedureState?.addComponent(mpiId, item);
            }}
          >
            <FBStarter index={0} />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const FBDeviationProcedure: React.FunctionComponent<FBDeviationProcedureProps> = ({
  deviationProcedureState,
  name,
  disabled,
  isPreview,
  isOutput,
  editorConfig,
  canEdit,
  ...props
}) => {
  const mpiEntries = useObserver(() => Object.entries(deviationProcedureState?.editorConfig ?? editorConfig ?? {}));

  return (
    <FBInput
      {...props}
      {...{ name }}
      type="deviationprocedure"
      shouldWrapWithFormControl={false}
    >
      <FBProvider deviationProcedureState={deviationProcedureState}>
        {mpiEntries.map(([mpiId, steps], index) => (
          <FBDeviationMpiSection
            key={mpiId}
            mpiId={mpiId}
            mpiIndex={index + 1}
            steps={steps}
            {...{ name: name ?? '', disabled, isPreview, isOutput, canEdit }}
            deviationProcedureState={deviationProcedureState}
          />
        ))}
      </FBProvider>
    </FBInput>
  );
};

export default withFBDeviationProcedure(FBDeviationProcedure);
